import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAppStatusActions } from '@/db/useAppStatusActions';
import { appStatusLiveState, storeUrlsEditDocState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AppStatusStoreUrls, Timestamp } from '@rock/types';
import { isHttpUrl } from '@rock/util';
import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { LiveSince } from './AppStatusGuidanceCard';
import { AppStatusStagingToolbar } from './AppStatusStagingToolbar';

const urlOrEmpty = z.string().refine((v) => !v.trim() || isHttpUrl(v.trim()), 'Must be a valid http(s) URL');

const FormSchema = z.object({
    ios: urlOrEmpty,
    android: urlOrEmpty,
});

type FormValues = z.infer<typeof FormSchema>;

function toFormValues(doc: AppStatusStoreUrls | undefined): FormValues {
    return {
        ios: doc?.ios ?? '',
        android: doc?.android ?? '',
    };
}

function toDoc(values: FormValues): AppStatusStoreUrls {
    return {
        object: 'app-status-store-urls',
        schema: 1,
        id: 'store-urls',
        ios: values.ios.trim() || undefined,
        android: values.android.trim() || undefined,
        updatedAt: new Date().toISOString() as Timestamp,
    };
}

/**
 * Durable store links — deliberately their own doc (not fields on required-upgrade) so clearing an
 * upgrade requirement never deletes where the app lives in the stores. Publishes independently of
 * the upgrade requirement.
 */
export const StoreLinksCard: FC = () => {
    const editDoc = useAsyncAtomValue(storeUrlsEditDocState, undefined);
    const live = useAsyncAtomValue(appStatusLiveState('store-urls'), undefined);
    const { busy, saveDraft } = useAppStatusActions('store-urls');

    const form = useForm<FormValues, unknown, FormValues>({
        resolver: zodResolver(FormSchema),
        defaultValues: toFormValues(undefined),
    });

    useEffect(() => {
        if (editDoc && !form.formState.isDirty) {
            form.reset(toFormValues(editDoc));
        }
    }, [editDoc, form]);

    const onSubmit = async (values: FormValues) => {
        await saveDraft(toDoc(values));
        form.reset(values);
    };

    return (
        <div className="space-y-4">
            <AppStatusStagingToolbar kind="store-urls" />
            <Card>
                <CardHeader>
                    <CardTitle>Store Links</CardTitle>
                    <CardDescription>
                        Where the "Update App" button sends users. The iOS URL (with the numeric App Store id) only
                        exists after first release — set it here the moment the app is live, no app update needed.
                        Android falls back to the Play Store URL for the app's package id when left blank; iOS has no
                        safe fallback, so a blank iOS URL hides the button for iOS users.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="ios"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>iOS App Store URL</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="bg-white"
                                                placeholder="https://apps.apple.com/app/id1234567890"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="android"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Android Play Store URL</FormLabel>
                                        <FormControl>
                                            <Input
                                                className="bg-white"
                                                placeholder="https://play.google.com/store/apps/details?id=app.rockhome"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Optional — the package-id fallback usually suffices.
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex items-center gap-4">
                                <Button type="submit" disabled={form.formState.isSubmitting || busy}>
                                    {form.formState.isSubmitting ? 'Saving…' : 'Save Draft'}
                                </Button>
                                <LiveSince live={live} />
                            </div>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
};
